import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getAgent } from "../../lib/api";
import "./AgentProfile.css";
import AgentCarriers from "../../Components/AgentCarriers/AgentCarriers";
import AgentLicenseMap from "../../Components/AgentLicenseMap/AgentLicenseMap";
import NotFound404 from "../Errors/NotFound404";

function AgentProfile() {
  const { slug } = useParams();
  const [agent, setAgent] = useState(null);

  // NEW
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Collapsible state for the "Licensed States" list
  const [showStates, setShowStates] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await getAgent(slug);
        setAgent(data || null);
      } catch (err) {
        console.error(err);
        setLoadError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // 🔧 Move hooks ABOVE any early returns
  const licensesByState = useMemo(() => {
    const arr = Array.isArray(agent?.licenses) ? agent.licenses : [];
    return Object.fromEntries(
      arr
        .filter((l) => l && l.state)
        .map((l) => [
          String(l.state).toUpperCase(),
          {
            label: l.label || l.type,
            number: l.number,
            status: l.status,
            expires: l.expires,
            link: l.pdfUrl || l.link,
          },
        ])
    );
  }, [agent]);

  // ✅ Calendar URL (uses agent.calendar with a safe default + normalization)
  const DEFAULT_CAL = "https://mymedahealth.com/quote";
  const calendarUrl = useMemo(() => {
    const raw = (agent?.calendar || DEFAULT_CAL).trim();
    return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  }, [agent]);

  if (loading) return <p>Loading...</p>;

  if (loadError || !agent) {
    return <NotFound404 reason="route" />;
  }

  const licenseCount = agent.licenses?.length ?? 0;

  if (licenseCount === 0) {
    return (
      <NotFound404
        reason="no-license"
        agentName={`${agent.firstName ?? ""} ${agent.lastName ?? ""}`.trim()}
        agentSlug={slug}
      />
    );
  }

  return (
    <div className="agent-page-container">
      <div className="agent-page-hero" />

      <div className="agent-profile">
        <div className="agent-profile-content">
          <div className="agent-profile-top">
            <img
              className="avatar"
              src={agent.headshotUrl}
              alt={`${agent.firstName} ${agent.lastName}`}
            />
            <div className="agent-profile-top-info">
              <h1 className="agent-name">
                {agent.firstName} {agent.lastName}
              </h1>
              {agent.title && <h2>{agent.title}</h2>}
              <p className="npn">NPN: {agent.npn}</p>
            </div>

            {/* ✅ Calendar link button */}
            <div className="agent-profile-top-contact">
              <a
                className="agent-contact-btn"
                href={calendarUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open calendar to contact ${agent.firstName} ${agent.lastName}`}
              >
                Get in Touch
              </a>
            </div>
          </div>

          {agent.bio && (
            <section className="agent-section">
              <h2 className="section-title">About Me</h2>
              <p className="agent-bio">{agent.bio}</p>
            </section>
          )}
        </div>

        <h1 className="states-title">Licensed States</h1>

        <div className="agent-state-map">
          <AgentLicenseMap
            licensesByState={licensesByState}
            onStateSelect={(postal, details) => {
              console.log("Selected", postal, details);
            }}
            height={520}
          />
        </div>

        {licenseCount > 0 && (
          <>
            <div className="states-header">
              <button
                type="button"
                className="states-toggle-btn"
                aria-expanded={showStates}
                aria-controls="stateList"
                onClick={() => setShowStates((s) => !s)}
              >
                <span className="chev" aria-hidden="true">
                  {showStates ? "▾" : "▸"}
                </span>
                {showStates ? "Hide" : "Show"} {licenseCount} state
                {licenseCount === 1 ? "" : "s"}
              </button>
            </div>

            <ul
              id="stateList"
              className={`state-list collapsible ${showStates ? "open" : ""}`}
            >
              {(agent.licenses ?? []).map((lic) => (
                <li className="states" key={lic.state}>
                  {lic.pdfUrl ? (
                    <a href={lic.pdfUrl} target="_blank" rel="noreferrer">
                      {lic.state?.toUpperCase?.() || lic.state} License
                    </a>
                  ) : (
                    <span>
                      {lic.state?.toUpperCase?.() || lic.state} License
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="agent-page-offerings" />
      <div className="agent-page-carriers">
        <AgentCarriers />
      </div>
    </div>
  );
}

export default AgentProfile;
